import { useId } from 'react';
import { mergePickerItems, type PickerItem } from '@/components/modals/formPickerItems';
import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../../components/ui/Dialog';
import { Button } from '../../../../../components/ui/Button';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import { Plus, Trash2 } from 'lucide-react';
import PickValueWithClassificationModal from '../../../../../components/modals/PickValueWithClassificationModal';
import Label from '@/components/Label';
import { AreaField } from '../../../../../components/formFields/AreaField';
import FormList from '../../../../../components/elements/FormList';
import HasValueModal from './HasValuesModal';
import { ReactSortable } from 'react-sortablejs';

type PropertiesModalProps = {
  visible: boolean;
  title: string;
  formProps?: any;
  onSubmit: (id: any, values: any) => void;
  onClose: () => void;
  classification?: any[];
};

const PropertiesModalForm = ({
  visible,
  title,
  formProps,
  onSubmit,
  onClose,
}: PropertiesModalProps) => {
  const [visibleHasValues, setVisibleHasValues] = useState(false);
  const [visibleAddBiblioDialog, setVisibleAddBiblioDialog] = useState(false);
  const [visibleAddUnitDialog, setVisibleAddUnitDialog] = useState(false);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    shortcut: Yup.string().nullable().required('Povinné pole'),
    description: Yup.string().nullable().required('Povinné pole'),
  });

  const pickerSession = useId();
  const formRef = useRef<any>(null);

  const handleAddHasValues = (values: any) => {
    if (!formRef.current) return;

    formRef.current.values.value_choices.push({
      ...values,
      order: formRef.current.values.value_choices.length + 1,
    });

    setVisibleHasValues(false);
  };

  const handleRemoveValueChoice = (valueChoice: any) => {
    if (!formRef.current) return;

    const newValues = formRef.current.values.value_choices.filter(
      (ele: any) => ele.order !== valueChoice.order
    );

    formRef.current.setFieldValue('value_choices', newValues);
  };

  const handleAddUnit = (item: any) => {
    formRef.current.setFieldValue('unit', item);

    setVisibleAddUnitDialog(false);
  };

  const handleDeleteUnit = () => {
    formRef.current.setFieldValue('unit', null);
  };

  const handleDeleteBiblio = (id: any) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.biblios.filter((biblio: any) => biblio.id !== id);

    formRef.current.setFieldValue('biblios', newValues);
  };

  const handleAddBiblio = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'biblios',
      mergePickerItems(formRef.current.values.biblios, items)
    );
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              shortcut: formProps?.shortcut || null,
              description: formProps?.description || null,
              biblios: formProps?.biblios || [],
              unit: formProps?.unit || null,
              value_choices: formProps?.value_choices || [],
            }}
            onSubmit={(values) => {
              onSubmit(formProps?.id, {
                ...values,
                biblios: values.biblios.map((biblio: any) => biblio.id),
                value_choices: values.value_choices,
                unit: values.unit?.id,
              });
            }}
          >
            {(props) => {
              const { values, setFieldValue } = props;
              return (
                <>
                  <Form>
                    <div className="space-y-1">
                      <Label name="title" title="Název" required />
                      <Field
                        name="title"
                        value={values.title || null}
                        placeholder="Název"
                        component={InputField}
                        data-testid="properties-modal-title-input"
                      />
                    </div>

                    <div>
                      <Label name="shortcut" title="Značka" required />
                      <Field
                        name="shortcut"
                        value={values.shortcut || null}
                        placeholder="Značka"
                        component={InputField}
                        data-testid="properties-modal-shortcut-input"
                      />
                    </div>

                    <div>
                      <Label name="description" title="Popisek" required />
                      <Field
                        name="description"
                        value={values.description || null}
                        placeholder="Popisek"
                        component={AreaField}
                        data-testid="properties-modal-description-input"
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Biblio"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddBiblioDialog(true)}
                            data-testid="properties-modal-add-biblio-btn"
                          />,
                        ]}
                      />
                      <FormList data={values.biblios} onDelete={handleDeleteBiblio} />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Jednotka"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddUnitDialog(true)}
                            data-testid="properties-modal-add-unit-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.unit ? [values.unit] : []}
                        onDelete={handleDeleteUnit}
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Nabývá hodnot"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleHasValues(true)}
                            data-testid="properties-modal-add-value-btn"
                          />,
                        ]}
                      />
                      <ReactSortable
                        list={values.value_choices}
                        setList={(items) => {
                          setFieldValue('value_choices', items);
                        }}
                        className="w-full overflow-hidden flex flex-col gap-1 text-color2 max-w-[530px]"
                        sort={true}
                      >
                        {(values.value_choices || []).map((val: any) => (
                          <div
                            className="flex justify-between py-1 px-2 gap-4 rounded-sm items-center bg-neutral-50 cursor-move"
                            key={val.order}
                          >
                            <div className="flex items-center gap-4 ">
                              <span className="min-w-[180px]">{val.value}</span>
                              <span>{val.description}</span>
                            </div>

                            <Trash2
                              key={1}
                              className="h-4 w-4"
                              onClick={() => handleRemoveValueChoice(val)}
                              data-testid={`properties-modal-remove-value-btn-${val.order}`}
                            />
                          </div>
                        ))}
                      </ReactSortable>
                    </div>
                  </Form>
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddBiblioDialog}
                    url="left-panel-biblio/"
                    pickerUrl="biblio/left-panel/"
                    onClose={() => setVisibleAddBiblioDialog(false)}
                    onSubmit={handleAddBiblio}
                    alreadySelectedValues={values.biblios}
                  />
                  <PickValueWithClassificationModal
                    multiple={false}
                    sessionKey={pickerSession}
                    visible={visibleAddUnitDialog}
                    url="left-panel-unit"
                    pickerUrl="unit/left-panel/"
                    onClose={() => setVisibleAddUnitDialog(false)}
                    onSubmit={handleAddUnit}
                  />
                  <HasValueModal
                    title="Přidat položku"
                    visible={visibleHasValues}
                    onClose={() => setVisibleHasValues(false)}
                    onSubmit={handleAddHasValues}
                  />
                </>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <Button
            key={1}
            variant="outline"
            onClick={() => onClose()}
            data-testid="properties-modal-close-btn"
          >
            Zavřít
          </Button>
          <Button
            key={2}
            onClick={() => onSubmitButton()}
            data-testid="properties-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function PropertiesModal(props: React.ComponentProps<typeof PropertiesModalForm>) {
  return props.visible ? (
    <PropertiesModalForm
      key={`${props.formProps?.id ?? 'create'}:${props.formProps?.id_version ?? ''}`}
      {...props}
    />
  ) : null;
}

export default PropertiesModal;
